import { create } from "zustand";
import { AppRoleDef, retrieveCompanyUsers, retrieveRoleDefs, updateInvitePref, updateRoles, UserAccess, UserInfo, UserResponse, adminInitiateUserPasswordResetAPI, forceDeleteUser as forceDeleteUserAPI } from "./actions"; // Import new API action
import { AppRole } from "../reg/actions";
import useUXStore from "../ux/store"; // Import UX store for toasts
import { USER_STATUS_AWAITING_RESET_STRING, USERS_PAGE_SIZE } from "../../constants"; // Import status constant

export interface Dictionary<T> {
    [key:string]: T | null
}

export const ACCESS_VERIFIED = "verified"
export const ACCESS_PENDING = "pending"

export interface UserFilters {
    branch?: string
    app?: string
    // Company being browsed. Only core1 admins can set this, and only while
    // cross-company browsing is enabled; the server re-checks every request.
    com?: string
}

type UserProp = {
    loading: boolean
    totalUsers: number
    users: Dictionary<UserInfo>
    roles: Dictionary<AppRoleDef> | null
    rolesLoaded: boolean
    currentPage: number
    searchQuery: string
    filters: UserFilters
    setCurrentPage: (page: number) => void
    setSearchQuery: (query: string) => void
    setFilters: (filters: UserFilters) => void
    forceDelete: (token: string, otp: string) => Promise<boolean>
    retrieveAllUsers : (page?: number, search?: string, filters?: UserFilters) => Promise<void>
    retrieveRoles : () => Promise<void>
    flagRolesUnloaded : () => void     // use to force reload roles
    updateRoles : (email:string,roles:AppRole[]) => void
    // check if users has specific role
    hasUserRole : (email:string, appId:string, roleId:string[]) => boolean
    getUserAccess : (email:string, app:string) => UserAccess | null
    // add user on frontend but not on server. Use useReg instead to call server
    addUser: (email:string,roles:UserAccess[]) => void
    updateUserPref: (lname:string,fname:string,userid:string) => Promise<string>
    adminInitiatePasswordReset: (userId: string) => Promise<boolean> // Add new store action
}

// Define a type for the items coming from the backend UserResponse
// This includes the overall_status field
type UserAccessWithOverallStatus = UserAccess & { overall_status: string };

export const useUser = create<UserProp>( (set,get) => ({
    loading: false,
    users: {},
    roles: null,
    rolesLoaded: false,
    totalUsers: 0,
    currentPage: 1,
    searchQuery: "",
    filters: {},
    flagRolesUnloaded: () => set({ rolesLoaded: false }),
    setCurrentPage: (page: number) => set({ currentPage: page }),
    setSearchQuery: (query: string) => set({ searchQuery: query }),
    setFilters: (filters: UserFilters) => set({ filters: filters }),
    async forceDelete(token: string, otp: string): Promise<boolean> {
        const { addToast } = useUXStore.getState()
        await forceDeleteUserAPI(token, otp)
        addToast("", "User and all their data were permanently deleted.")

        // The list just shrank, so the current page may no longer exist.
        const { currentPage, searchQuery, filters, totalUsers } = get()
        const remaining = Math.max(totalUsers - 1, 0)
        const lastPage = Math.max(Math.ceil(remaining / USERS_PAGE_SIZE), 1)
        await get().retrieveAllUsers(Math.min(currentPage, lastPage), searchQuery, filters)
        return true
    },
    async retrieveAllUsers(page = 1, search?: string, filters?: UserFilters) {
        if (get().loading) return

        get().retrieveRoles()
        set( state => ({loading:true, currentPage: page}))
        // Calculate offset from page number
        const offset = (page - 1) * USERS_PAGE_SIZE
        // Call with search and filter parameters
        let res: UserResponse
        try {
            res = await retrieveCompanyUsers({
                offset,
                prefs: true,
                search: search || get().searchQuery,
                branchFilter: filters?.branch || get().filters.branch,
                appFilter: filters?.app || get().filters.app,
                com: filters?.com ?? get().filters.com
            })
        } catch (err) {
            // Without this the loading flag stays raised and the guard above blocks
            // every later retrieval, leaving the list permanently stuck
            console.log(err)
            set( state => ({loading:false}))
            return
        }

        const userDic:Dictionary<UserInfo> = {}
        // Explicitly type 'item' here based on the expected structure from backend
        res.users.forEach( (item: UserAccessWithOverallStatus) => { 
            let userItem = userDic[item.email];
            if (!userItem) {
                userItem = {
                    email: item.email, 
                    userid: item.userid, 
                    username: item.username,
                    pref: null, 
                    access: [{ 
                        app: item.app,
                        code: item.code,
                        email: item.email,
                        status: item.status, // This is the access-specific status
                        com: item.com,
                        userid: item.userid,
                        username: item.username,
                        roles: item.roles,
                    }],
                    status: item.overall_status // Use item.overall_status for overall status
                };
            } else {
                userItem.access.push({ 
                    app: item.app,
                    code: item.code,
                    email: item.email,
                    status: item.status, 
                    com: item.com,
                    userid: item.userid,
                    username: item.username,
                    roles: item.roles,
                });
                userItem.status = item.overall_status; // Ensure overall status is updated if userItem already exists
                // An access record only carries a userid once its invite is accepted,
                // so take the identity from whichever record does have one
                if (!userItem.userid && item.userid) {
                    userItem.userid = item.userid;
                    userItem.username = item.username;
                }
            }
            userDic[item.email] = userItem;
        });

        if (res.prefs) {
            res.prefs.forEach( prefItem => { // Renamed item to prefItem to avoid conflict
                const user = Object.values(userDic).find( u => u?.userid === prefItem.userid);
                if (user) { 
                    user.pref = prefItem;
                    userDic[user.email] = user;
                }
            })
        }
        set( state => ({loading:false,totalUsers:res.total, users:userDic}))
        return
    },
    async updateRoles(email,roles) {
        const res = await updateRoles(email, roles)
        if (res.Code === "200") {
            let resAcc = res.Msg.access
            const userDic = {...get().users}
            if (!resAcc) {
                resAcc = []
            }
            if (userDic[email]) { // Check if userDic[email] exists
                userDic[email]!.access = resAcc
                set( state => ({users:userDic}))
            }
        }
        return res.Code
    },
    async retrieveRoles() {
        if (get().roles !== null && get().rolesLoaded) return
        
        set( state => ({rolesLoaded:true}))
        const res = await retrieveRoleDefs()
        let roles:Dictionary<AppRoleDef> = {} // Initialize roles as Dictionary<AppRoleDef>
        roles!["global"] = {id:"global", name:"Global", roles: res.global}
        res.apps.forEach( val => roles![val.id] = val)
        set( state => ({roles:roles}))
    },
    hasUserRole(email, appId, roleId) {
        const users = get().users[email]
        if (!users || users.access === null) return false // Check if users or users.access is null
        
        for (const access of users.access!) {
            if (access.app === appId) {
                for (const role of access.roles) {
                    for (const crole of roleId) {
                        if (role === crole)
                            return true
                    }
                }
                return false
            }
        }
        return false
    },
    getUserAccess(email, app) {
        const users = get().users[email]
        if (!users || !users.access) return null // Check if users or users.access is null
        return users.access.find( val => val.app === app)!
    },
    addUser(email, roles) {
        const users = {...get().users, [email]:{
            email:email,
            access: roles,
            status: '' // Add default status or fetch appropriately
        }}
        set( state => ({users: users}))
    },
    
    async updateUserPref(lname, fname, userid) {
        return updateInvitePref(lname, fname, userid)
    },

    // New action to initiate password reset for a user
    adminInitiatePasswordReset: async (username: string): Promise<boolean> => {
        const { addToast } = useUXStore.getState(); // Access toast function
        try {
            await adminInitiateUserPasswordResetAPI(username); // Call the new API action
            // Update local state for the specific user
            set(state => {
                const updatedUsers = { ...state.users };
                // Find the user by userid (iterate through values as keys are emails)
                const userToUpdate = Object.values(updatedUsers).find(u => u?.username === username);
                if (userToUpdate && userToUpdate.email) { // Ensure userToUpdate and email are not null
                    // Update the status to 'awaiting_reset'
                    updatedUsers[userToUpdate.email] = { ...userToUpdate, status: USER_STATUS_AWAITING_RESET_STRING };
                }
                return { users: updatedUsers };
            });
            addToast("", "Password reset initiated successfully."); // Success toast
            return true;
        } catch (error) {
            console.error("Failed to initiate password reset:", error);
            addToast("Failed", "Failed to initiate password reset."); // Error toast
            return false;
        }
    }
}))
